import type { FormField } from '../../store/slice';
import { END_OF_LIFE_MOMENT_TYPE } from '../../utils/eolCreatePayload';

/** Stages row: group_component with per-stage customer notification date (not release phases). */
export function isEolStageRowDefinition(field: unknown): boolean {
  const f = field as Partial<FormField> & {
    fieldGroup?: Array<{ name?: string; type?: string }>;
  };
  if (!f || f.type !== 'group_component' || !Array.isArray(f.fieldGroup)) {
    return false;
  }
  if (f.fieldGroup.some(gf => gf?.name === 'phaseDate')) {
    return false;
  }
  return f.fieldGroup.some(
    gf =>
      gf?.type === 'datepicker' &&
      typeof gf?.name === 'string' &&
      gf.name.endsWith('CustomerNotificationDate')
  );
}

export function getEolStageGroupFields(
  formFields: FormField[],
  momentTypeId: string | null | undefined,
  stagesSectionHeading = 'Stages'
): FormField[] {
  if (momentTypeId !== END_OF_LIFE_MOMENT_TYPE) return [];

  return formFields.filter(f => {
    if ((f.section || '') !== stagesSectionHeading) return false;
    return isEolStageRowDefinition(f);
  });
}

export function getEolCustomerNotificationFieldName(
  stageGroup: FormField
): string | undefined {
  const fg = stageGroup.fieldGroup;
  if (!Array.isArray(fg)) return undefined;
  const nested = fg.find(
    gf =>
      gf?.type === 'datepicker' &&
      typeof gf?.name === 'string' &&
      gf.name.endsWith('CustomerNotificationDate')
  );
  return nested?.name;
}

export function getEolEffectiveDateFieldName(
  stageGroup: FormField
): string | undefined {
  const fg = stageGroup.fieldGroup;
  if (!Array.isArray(fg)) return undefined;
  const nested = fg.find(
    gf =>
      gf?.type === 'datepicker' &&
      typeof gf?.name === 'string' &&
      gf.name.endsWith('EffectiveDate')
  );
  return nested?.name;
}

/** True if date value counts as filled (same semantics as release phase dates). */
export type StageDatePresence = (v: unknown) => boolean;

export function isEolStageRowComplete(
  stageGroup: FormField,
  groupVals: Record<string, unknown> | undefined,
  isDatePresent: StageDatePresence
): boolean {
  const notificationKey = getEolCustomerNotificationFieldName(stageGroup);
  const effectiveKey = getEolEffectiveDateFieldName(stageGroup);
  if (!notificationKey || !effectiveKey) return false;
  return (
    isDatePresent(groupVals?.[notificationKey]) &&
    isDatePresent(groupVals?.[effectiveKey])
  );
}

export function validateEolStagesAtLeastOne(
  stageGroups: FormField[],
  values: Record<string, unknown>,
  isDatePresent: StageDatePresence
): {
  isValid: boolean;
  emptyGroupNames: string[];
  partialGroupNames: string[];
} {
  if (!stageGroups.length) {
    return { isValid: true, emptyGroupNames: [], partialGroupNames: [] };
  }

  const anyComplete = stageGroups.some(group => {
    const groupVals = values[group.name] as Record<string, unknown> | undefined;
    return isEolStageRowComplete(group, groupVals, isDatePresent);
  });

  if (anyComplete) {
    return { isValid: true, emptyGroupNames: [], partialGroupNames: [] };
  }

  const partialGroupNames: string[] = [];
  stageGroups.forEach(group => {
    const groupVals = values[group.name] as Record<string, unknown> | undefined;
    const notificationKey = getEolCustomerNotificationFieldName(group);
    const effectiveKey = getEolEffectiveDateFieldName(group);
    const notificationPresent = notificationKey
      ? isDatePresent(groupVals?.[notificationKey])
      : false;
    const effectivePresent = effectiveKey
      ? isDatePresent(groupVals?.[effectiveKey])
      : false;
    if (notificationPresent !== effectivePresent && group.name) {
      partialGroupNames.push(group.name);
    }
  });

  return {
    isValid: false,
    emptyGroupNames: stageGroups.map(g => g.name).filter(Boolean) as string[],
    partialGroupNames
  };
}

function queryNestedDateField(
  groupEl: Element | null,
  fieldName: string
): Element | null {
  return (
    groupEl?.querySelector(`[data-field-name="${fieldName}"]`) ??
    document.querySelector(`[data-field-name="${fieldName}"]`)
  );
}

export const FIELD_VALIDATION_ERROR_ATTR = 'data-validation-error';

export function setFieldErrorBorder(el: Element | null): void {
  if (!el) return;
  const computedStyle = window.getComputedStyle(el);
  if (
    computedStyle.display === 'none' ||
    computedStyle.visibility === 'hidden'
  ) {
    return;
  }
  el.setAttribute(FIELD_VALIDATION_ERROR_ATTR, 'true');
}

export function removeFieldErrorBorder(el: Element | null): void {
  if (!el) return;
  el.removeAttribute(FIELD_VALIDATION_ERROR_ATTR);
}

export function updateEolStageDomErrors(
  stageGroups: FormField[],
  values: Record<string, unknown>,
  showErrors: boolean,
  isDatePresent: StageDatePresence,
  dom: {
    addRequiredErrorMessage: (el: Element | null) => void;
    removeExistingErrorMessage: (el: Element | null) => void;
    setFieldErrorBorder: (el: Element | null) => void;
    removeFieldErrorBorder: (el: Element | null) => void;
  }
): void {
  const { isValid, emptyGroupNames, partialGroupNames } =
    validateEolStagesAtLeastOne(stageGroups, values, isDatePresent);
  const hasPartialStages = partialGroupNames.length > 0;
  const {
    addRequiredErrorMessage,
    removeExistingErrorMessage,
    setFieldErrorBorder,
    removeFieldErrorBorder
  } = dom;

  stageGroups.forEach(group => {
    const notificationKey = getEolCustomerNotificationFieldName(group);
    const effectiveKey = getEolEffectiveDateFieldName(group);
    if (!notificationKey || !effectiveKey) return;

    const groupEl = document.querySelector(`[data-field-name="${group.name}"]`);
    const notificationEl = queryNestedDateField(groupEl, notificationKey);
    const effectiveEl = queryNestedDateField(groupEl, effectiveKey);

    const groupVals = values[group.name] as Record<string, unknown> | undefined;
    const rowComplete = isEolStageRowComplete(group, groupVals, isDatePresent);
    const notificationPresent = isDatePresent(groupVals?.[notificationKey]);
    const effectivePresent = isDatePresent(groupVals?.[effectiveKey]);

    if (!showErrors) {
      // Clear both messages and borders when errors are suppressed
      removeExistingErrorMessage(notificationEl);
      removeExistingErrorMessage(effectiveEl);
      removeFieldErrorBorder(notificationEl);
      removeFieldErrorBorder(effectiveEl);
      return;
    }

    const isPartialGroup = partialGroupNames.includes(group.name);
    const inFailureSet = emptyGroupNames.includes(group.name);

    if (rowComplete || isValid) {
      // Stage complete or overall valid — clear all errors
      removeExistingErrorMessage(notificationEl);
      removeExistingErrorMessage(effectiveEl);
      removeFieldErrorBorder(notificationEl);
      removeFieldErrorBorder(effectiveEl);
    } else if (hasPartialStages && isPartialGroup) {
      // Exactly one field is missing — anchor message and border to that field only
      if (!notificationPresent) {
        addRequiredErrorMessage(notificationEl);
        setFieldErrorBorder(notificationEl);
        removeExistingErrorMessage(effectiveEl);
        removeFieldErrorBorder(effectiveEl);
      } else {
        removeExistingErrorMessage(notificationEl);
        removeFieldErrorBorder(notificationEl);
        addRequiredErrorMessage(effectiveEl);
        setFieldErrorBorder(effectiveEl);
      }
    } else if (!hasPartialStages && inFailureSet) {
      // All stages untouched — message on notificationEl, borders on both empty fields
      removeExistingErrorMessage(effectiveEl);
      addRequiredErrorMessage(notificationEl);
      setFieldErrorBorder(notificationEl);
      setFieldErrorBorder(effectiveEl);
    } else {
      // Stage is empty but another stage is partial — clear errors on this one
      removeExistingErrorMessage(notificationEl);
      removeExistingErrorMessage(effectiveEl);
      removeFieldErrorBorder(notificationEl);
      removeFieldErrorBorder(effectiveEl);
    }
  });
}

export function eolStageChangeAffectsValidation(
  stageGroups: FormField[],
  changedFieldName: string
): boolean {
  if (!stageGroups.length) return false;
  if (stageGroups.some(g => g.name === changedFieldName)) return true;
  return stageGroups.some(g =>
    Array.isArray(g.fieldGroup)
      ? g.fieldGroup.some(gf => gf?.name === changedFieldName)
      : false
  );
}

/** EOL Overview rich text field ids (matches form.json id / name). */
export const EOL_LAUNCH_RICHTEXT_FIELD_IDS = [
  'launchSummary',
  'launchRegion'
] as const;

export function isEolLaunchRichTextField(fieldIdOrName: string): boolean {
  return (EOL_LAUNCH_RICHTEXT_FIELD_IDS as readonly string[]).includes(
    fieldIdOrName
  );
}

export function isEolLaunchRichTextEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value !== 'string') return true;
  if (value.trim() === '') return true;
  const textContent = value.replace(/<[^>]*>/g, '').trim();
  return textContent === '';
}

function queryFieldById(fieldId: string): Element | null {
  return (
    document.querySelector(`[data-field-id="${fieldId}"]`) ??
    document.querySelector(`[data-field-name="${fieldId}"]`)
  );
}

export function updateEolLaunchRichTextDomErrors(
  values: Record<string, unknown>,
  showErrors: boolean,
  momentTypeId: string | null | undefined,
  dom: {
    setFieldErrorBorder: (el: Element | null) => void;
    removeFieldErrorBorder: (el: Element | null) => void;
  }
): void {
  if (momentTypeId !== END_OF_LIFE_MOMENT_TYPE) return;

  const { setFieldErrorBorder, removeFieldErrorBorder } = dom;

  EOL_LAUNCH_RICHTEXT_FIELD_IDS.forEach(fieldId => {
    const fieldEl = queryFieldById(fieldId);
    const isEmpty = isEolLaunchRichTextEmpty(values[fieldId]);

    if (!showErrors || !isEmpty) {
      removeFieldErrorBorder(fieldEl);
    } else {
      setFieldErrorBorder(fieldEl);
    }
  });
}

export function eolLaunchRichTextChangeAffectsValidation(
  changedFieldName: string
): boolean {
  return isEolLaunchRichTextField(changedFieldName);
}
